import {type List} from 'immutable';

import {createAction, createAllActions} from 'web-app/util/redux/typed-action-helpers';

import {type DirectoryFolder, type File, type Folder} from './reducer';

const domain = 'DOCUMENTS';

export const {
    action: fetchData,
    success: fetchDataSuccess,
    failed: fetchDataFailed,
} = createAllActions(
    `${domain}_FETCH_DATA`,
    (institutionId: string, folderId?: string) => ({institutionId, folderId}),
    (institutionId: string, folderId: string, result: {files: List<File>; folders: List<Folder>; folder: Folder}) => ({
        institutionId,
        folderId,
        result,
    }),
    (error: Error) => ({error}),
);

export const {
    action: fetchStructure,
    success: fetchStructureSuccess,
    failed: fetchStructureFailed,
} = createAllActions(
    `${domain}_GET_FOLDER_STRUCTURE`,
    (institutionId: string, folderId: string | null) => ({
        institutionId,
        folderId,
    }),
    (
        institutionId: string,
        folderId: string | null,
        result: {folder: DirectoryFolder; folders: List<DirectoryFolder>},
    ) => ({
        institutionId,
        folderId,
        result,
    }),
    (error: Error) => ({error}),
);

export const {
    action: deleteFile,
    success: deleteFileSuccess,
    failed: deleteFileFailed,
    aborted: deleteFileAborted,
} = createAllActions(
    `${domain}_DELETE_FILE`,
    (filePersistenceId: string) => ({filePersistenceId}),
    (filePersistenceId: string) => ({filePersistenceId}),
    (error: Error) => ({error}),
);

export const {action: startMoveFile} = createAction(`${domain}_MOVE_FILE_START`, (file: File) => ({file}));

export const {
    action: moveFile,
    success: moveFileSuccess,
    failed: moveFileFailed,
    aborted: moveFileAborted,
} = createAllActions(
    `${domain}_MOVE_FILE`,
    (institutionId: string, filePersistenceId: string, folderId: string, url: string) => ({
        institutionId,
        filePersistenceId,
        folderId,
        url,
    }),
    () => ({}),
    (error: Error) => ({error}),
);

export const {action: toggleFileUpload} = createAction(`${domain}_TOGGLE_FILE_UPLOAD`, (show: boolean) => ({show}));

export const {action: showCreateFolder} = createAction(
    `${domain}_SHOW_CREATE_FOLDER`,
    (show: boolean, folderId?: string | null) => ({show, folderId}),
);

export const {
    action: createFolder,
    success: createFolderSuccess,
    failed: createFolderFailed,
} = createAllActions(
    `${domain}_CREATE_FOLDER`,
    (institutionId: string, title: string, parent: string | null) => ({
        institutionId,
        title,
        parent,
    }),
    (institutionId: string, title: string, parent: string, result: {folder: Folder & DirectoryFolder}) => ({
        institutionId,
        title,
        parent,
        result,
    }),
    (error: Error) => ({error}),
);

export const {action: showRenameFolder} = createAction(
    `${domain}_SHOW_RENAME_FOLDER`,
    (show: boolean, folderId?: string) => ({show, folderId}),
);

export const {
    action: renameFolder,
    success: renameFolderSuccess,
    failed: renameFolderFailed,
} = createAllActions(
    `${domain}_RENAME_FOLDER`,
    (institutionId: string, folderId: string, title: string) => ({
        institutionId,
        folderId,
        title,
    }),
    (institutionId: string, folderId: string, title: string, result: {folder: Folder}) => ({
        institutionId,
        folderId,
        title,
        result,
    }),
    (error: Error) => ({error}),
);

export const {
    action: deleteFolder,
    success: deleteFolderSuccess,
    failed: deleteFolderFailed,
    aborted: deleteFolderAborted,
} = createAllActions(
    `${domain}_DELETE_FOLDER`,
    (institutionId: string, folderId: string) => ({
        institutionId,
        folderId,
    }),
    (institutionId: string, folderId: string) => ({
        institutionId,
        folderId,
    }),
    (error: Error) => ({error}),
);

export const {
    action: createFile,
    success: createFileSuccess,
    failed: createFileFailed,
} = createAllActions(
    `${domain}_CREATE_FILE`,
    (
        institutionId: string,
        fileId: string,
        title: string,
        description: string,
        folderId: string | null,
        formName: string,
    ) => ({institutionId, fileId, title, description, folderId, formName}),
    (result: {file: Partial<File>}) => ({result}),
    (error: Error) => ({error}),
);

export const {action: fileAdded} = createAction(`${domain}_FILE_ADDED`, () => ({}));

export const {action: showRenameFile} = createAction(
    `${domain}_SHOW_RENAME_FILE`,
    (show: boolean, filePersistenceId?: string) => ({show, filePersistenceId}),
);

export const {
    action: renameFile,
    success: renameFileSuccess,
    failed: renameFileFailed,
} = createAllActions(
    `${domain}_RENAME_FILE`,
    (institutionId: string, filePersistenceId: string, title: string, description: string) => ({
        institutionId,
        filePersistenceId,
        title,
        description,
    }),
    (institutionId: string, filePersistenceId: string, title: string, description: string, result: {file: File}) => ({
        institutionId,
        filePersistenceId,
        title,
        description,
        result,
    }),
    (error: Error) => ({error}),
);
